import { yupResolver } from '@hookform/resolvers'
import CardContents from '@ifca-root/react-component/src/components/CardList/CardContents'
import MainHeader from '@ifca-root/react-component/src/components/Header/MainHeader'
import { ContentWrapper } from '@ifca-root/react-component/src/components/Layout/ContentWrapper'
import Loading from '@ifca-root/react-component/src/components/Loading/Loading'
import { Checkbox, TextField } from '@material-ui/core'
import { Autocomplete } from '@material-ui/lab'
import { ErrorDialog } from 'components/Dialog/ErrorDialog'
import { AccCodeDropdownFullWidth } from 'components/Dropdown/AccCodeDropdown'
import { AccountFooter } from 'components/Footer/AccountFooter'
import {
  useGetAccountPeriodLazyQuery,
  useGetAssignedCompanyQuery,
  useGetCompanyNameQuery,
  useGetEntityCoaLazyQuery,
  useGetMasterCoaQuery,
} from 'generated/graphql'
import { CommonYupValidation } from 'helpers/Form/YupValidation'
import { formatDate } from 'helpers/StringNumberFunction/FormatDate'
import React, { useEffect, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useHistory, useParams } from 'react-router'
import * as yup from 'yup'
import { useQuery } from '@tanstack/react-query'

interface StatementOfComprehensiveIncomeByEntityParamsFormProps {
  CompanyID: string
  Year: Number
  Period: Number
  AccountCode: string[]
  AccountType: string[]
}

export interface MasterCOA {
  Name: string
  MasterCOAID: string
  ParentMasterCOAID: string
  IsControl: boolean
  IsLastNode: boolean
  AccountType: string
  Code: string
  Level: number
}

export interface AccountType {
  name: string
  value: string
}

interface CompanyOption {
  Name: string
  CompanyID: string
}

const PARAMS_SCHEMA = yup.object().shape({
  CompanyID: CommonYupValidation.requireField('Company is required'),
  Year: CommonYupValidation.requireField('Year is required'),
  Period: CommonYupValidation.requireField('Period is required'),
})

const ACCOUNT_TYPE_OPTIONS: AccountType[] = [
  { name: 'Revenue', value: 'REVENUE' },
  { name: 'Expenses', value: 'EXPENSES' },
]

export const StatementOfComprehensiveIncomeByEntity = (props: any) => {
  let history = useHistory()
  const { CompanyID }: any = useParams()
  const user = JSON.parse(localStorage.getItem('loggedInUser'))
  const latestCompany = localStorage.getItem('latestCompany')
  const [masterCOAData, setMasterCOAData] = useState<MasterCOA[]>([])
  const [errMessage, setErrMessage] = useState(null)
  const [errDialog, setErrDialog] = useState(false)

  const { handleSubmit, register, errors, control, watch, setValue } = useForm<
    StatementOfComprehensiveIncomeByEntityParamsFormProps
  >({
    defaultValues: {},
    mode: 'all',
    resolver: yupResolver(PARAMS_SCHEMA),
  })

  // ACCOUNTX API CALLS
  const {
    loading: CompanyLoading,
    error: CompanyError,
    data: { getCompany } = { getCompany: [] },
  } = useGetCompanyNameQuery({
    onError: ({ message }) => {
      let error = message?.substring(15)
      setErrMessage(error)
      setErrDialog(true)
    },
    variables: { CompanyID },
    fetchPolicy: 'network-only',
  })

  const {
    loading: CompanyAssignedLoading,
    error: CompanyAssignedError,
    data: { getAssignedCompanyByUser } = { getAssignedCompanyByUser: [] },
  } = useGetAssignedCompanyQuery({
    onError: ({ message }) => {
      let error = message?.substring(15)
      setErrMessage(error)
      setErrDialog(true)
    },
    fetchPolicy: 'network-only',
  })

  const [
    loadAccountPeriod,
    {
      loading: accountPeriodLoading,
      data: { getAccountPeriod } = { getAccountPeriod: [] },
    },
  ] = useGetAccountPeriodLazyQuery({
    onError: ({ message }) => {
      let error = message?.substring(15)
      setErrMessage(error)
      setErrDialog(true)
    },
    fetchPolicy: 'network-only',
  })

  //MasterCOA
  const {
    loading: MasterCOALoading,
    error: MasterCOAError,
    data: { getMasterCOA } = { getMasterCOA: [] },
  } = useGetMasterCoaQuery({
    onError: ({ message }) => {
      let error = message?.substring(15)
      setErrMessage(error)
      setErrDialog(true)
    },
    fetchPolicy: 'network-only',
    variables: { IsLastNode: true },
  })

  const [
    loadEntityCOA,
    {
      loading: EntityCOALoading,
      data: { getEntityCOA } = { getEntityCOA: [] },
    },
  ] = useGetEntityCoaLazyQuery({
    onError: ({ message }) => {
      let error = message?.substring(15)
      setErrMessage(error)
      setErrDialog(true)
    },
    fetchPolicy: 'network-only',
    onCompleted: data => {
      const masterCOAList =
        data?.getEntityCOA?.length > 0
          ? data?.getEntityCOA
              ?.map(coa => coa?.MasterCOA)
              ?.filter(
                coa => coa?.IsLastNode === true && coa?.IsControl === false
              )
          : getMasterCOA?.filter(
              coa => coa?.IsLastNode === true && coa?.IsControl === false
            )
      setMasterCOAData((masterCOAList as any) as MasterCOA[])
    },
  })

  useEffect(() => {
    if (!latestCompany) return

    loadAccountPeriod({
      variables: {
        CompanyID: latestCompany,
        orderByAsc: 'FYear',
      },
    })

    loadEntityCOA({
      variables: { CompanyID: latestCompany },
    })
  }, [latestCompany, loadAccountPeriod, loadEntityCOA])

  const selectedCompanyID = watch('CompanyID')
  const selectedYear = watch('Year')
  const selectedAccountCode = watch('AccountCode') || []
  const selectedAccountType = watch('AccountType') || []

  const companyOptions: CompanyOption[] = useMemo(
    () =>
      (!!user?.superUser === false ? getAssignedCompanyByUser : getCompany) ||
      [],
    [user?.superUser, getAssignedCompanyByUser, getCompany]
  )

  const defaultCompany = useMemo(
    () => companyOptions?.find(x => x?.CompanyID === latestCompany) || null,
    [companyOptions, latestCompany]
  )

  const yearOptions = useMemo(() => {
    if (!selectedCompanyID) return []
    return Array.from(new Set((getAccountPeriod || []).map(el => el?.FYear)))
      .filter(Boolean)
      .sort((a: any, b: any) => b - a)
  }, [selectedCompanyID, getAccountPeriod])

  const financialAccountPeriods = useMemo(() => {
    return (getAccountPeriod || [])
      .filter(x => x?.FYear === selectedYear)
      .sort((a, b) => a.FPeriod - b.FPeriod)
  }, [getAccountPeriod, selectedYear])

  const selectedAccountCodeOptions = useMemo(() => {
    if (!selectedAccountCode?.length) return []
    const selected = new Set(selectedAccountCode)
    return masterCOAData.filter(x => selected.has(x?.Code))
  }, [masterCOAData, selectedAccountCode])

  const selectedAccountTypeOptions = useMemo(() => {
    if (!selectedAccountType?.length) return []
    const selected = new Set(selectedAccountType)
    return ACCOUNT_TYPE_OPTIONS.filter(x => selected.has(x.value))
  }, [selectedAccountType])

  const onSubmit = (data, status) => {
    history.push({
      pathname: `/digital-reports/general-ledger/statement-income-entity/generated`,
      state: {
        CompanyID: data?.CompanyID,
        Year: Number(data.Year),
        Period: Number(data.Period),
        AccountType:
          !!data?.AccountType && data?.AccountType?.length
            ? data?.AccountType?.join(',')
            : undefined,
        AccountCode:
          !!data?.AccountCode && data?.AccountCode?.length
            ? data?.AccountCode?.join(',')
            : undefined,
      },
    })
  }

  return (
    <>
      {CompanyLoading && <Loading />}
      {EntityCOALoading && <Loading />}
      {accountPeriodLoading && <Loading />}
      {CompanyAssignedLoading && <Loading />}

      <MainHeader
        mainBtn="back"
        onClick={() => history.push(`/digital-reports/general-ledger`)}
        smTitle={'General Ledger'}
        title={user?.companyName}
        routeSegments={[
          { name: 'Main Menu' },
          { name: 'Submenu' },
          { name: 'StatementOfComprehensiveIncomeByEntity', current: true },
        ]}
        rightRouteSegments={[{ name: 'Parameters', current: true }]}
      />
      <ContentWrapper float>
        <CardContents>
          {!CompanyAssignedLoading && !CompanyLoading && (
            <Controller
              render={({ value, onChange }) => {
                return (
                  <Autocomplete
                    options={companyOptions}
                    getOptionLabel={(option: CompanyOption) => option.Name}
                    fullWidth
                    onChange={(value, newValue: any) => {
                      setValue('CompanyID', newValue?.CompanyID)

                      loadAccountPeriod({
                        variables: {
                          CompanyID: newValue?.CompanyID,
                          orderByAsc: 'FYear',
                        },
                      })

                      loadEntityCOA({
                        variables: { CompanyID: newValue?.CompanyID },
                      })
                    }}
                    renderOption={(props, option) => {
                      return <span>{props?.Name}</span>
                    }}
                    defaultValue={defaultCompany}
                    renderInput={(params: any) => {
                      return (
                        <div>
                          <TextField
                            {...params}
                            helperText={errors?.CompanyID?.message}
                            error={errors?.CompanyID ? true : false}
                            label="Company"
                            style={{ width: '100%' }}
                            margin="normal"
                            required
                            defaultValue={defaultCompany}
                          />
                        </div>
                      )
                    }}
                  />
                )
              }}
              label="Company"
              name="CompanyID"
              autoComplete="off"
              control={control}
              multiline={true}
              fullWidth
              margin="normal"
              ref={register}
              helperText={errors?.CompanyID?.message}
              error={errors?.CompanyID ? true : false}
              required
              defaultValue={latestCompany}
            />
          )}

          {!accountPeriodLoading && (
            <Controller
              render={({ value, onChange }) => {
                return (
                  <Autocomplete
                    options={yearOptions}
                    getOptionLabel={option => `${option}`}
                    noOptionsText="Please set up Account Period"
                    fullWidth
                    onChange={(value, newValue: any) => {
                      setValue('Year', newValue)
                    }}
                    renderOption={(props, option) => {
                      return <span>{props}</span>
                    }}
                    renderInput={(params: any) => {
                      return (
                        <div>
                          <TextField
                            {...params}
                            error={errors?.Year ? true : false}
                            label="Financial Year"
                            style={{ width: '100%' }}
                            margin="normal"
                          />
                        </div>
                      )
                    }}
                  />
                )
              }}
              label="Financial Year"
              name="Year"
              autoComplete="off"
              control={control}
              multiline={true}
              fullWidth
              margin="normal"
              ref={register}
              error={errors?.Year ? true : false}
            />
          )}

          {/* <Controller
              render={({ value, onChange }) => {
                return (
                  <Autocomplete
                    options={
                      getAccountPeriod
                        ?.filter(x => x?.FYear === Number(watch('Year')))
                        .sort((a, b) => b.FPeriod - a.FPeriod) || []
                    }
                    getOptionLabel={option => `${option?.FPeriod}`}
                    fullWidth
                    onChange={(value, newValue: any) => {
                      setValue('Period', newValue?.FPeriod)
                    }}
                    renderOption={(props, option) => {
                      return <span>{props?.FPeriod}</span>
                    }}
                    renderInput={(params: any) => {
                      return (
                        <div>
                          <TextField
                            {...params}
                            error={errors?.Period ? true : false}
                            label="Period"
                            style={{ width: '100%' }}
                            margin="normal"
                          />
                        </div>
                      )
                    }}
                  />
                )
              }}
              label="Period"
              name="Period"
              autoComplete="off"
              control={control}
              multiline={true}
              fullWidth
              margin="normal"
              ref={register}
              error={errors?.Period ? true : false}
            /> */}

          <Controller
            render={({ value, onChange }) => {
              return (
                <Autocomplete
                  options={financialAccountPeriods}
                  getOptionLabel={option => String(option?.FPeriod)}
                  noOptionsText="Please set up Account Period"
                  fullWidth
                  value={
                    financialAccountPeriods.find(
                      option => option?.FPeriod === value
                    ) || null
                  }
                  onChange={(value, newValue: any) => {
                    onChange(newValue?.FPeriod)
                  }}
                  renderOption={(props, option) => {
                    return (
                      <span className="">
                        {`${props?.FPeriod} | ${formatDate(
                          props?.StartDate
                        )} - ${formatDate(props?.EndDate)}`}
                      </span>
                    )
                  }}
                  renderInput={(params: any) => {
                    return (
                      <div>
                        <TextField
                          {...params}
                          label="Financial Period"
                          style={{ width: '100%' }}
                          margin="dense"
                          type="number"
                        />
                      </div>
                    )
                  }}
                />
              )
            }}
            label="Financial Period"
            name="Period"
            autoComplete="off"
            control={control}
            multiline={true}
            fullWidth
            margin="normal"
            ref={register}
            required
            error={errors?.Period ? true : false}
          />

          {!EntityCOALoading && !MasterCOALoading && (
            <Controller
              name="AccountCode"
              label="Account Code"
              fullWidth
              margin="dense"
              ref={register}
              control={control}
              render={({ onChange, onBlur, value }) => {
                return (
                  <Autocomplete
                    multiple
                    value={selectedAccountCodeOptions as MasterCOA[]}
                    fullWidth
                    options={masterCOAData}
                    disableCloseOnSelect
                    getOptionLabel={option =>
                      `${option?.Name} | ${option?.Code}`
                    }
                    PopperComponent={AccCodeDropdownFullWidth}
                    onChange={(value, newValue: any) => {
                      onChange(newValue?.map(x => x?.Code))
                    }}
                    renderInput={params => (
                      <TextField
                        {...params}
                        variant="standard"
                        label="Account Code"
                      />
                    )}
                    renderOption={(option, { selected }) => {
                      return (
                        <>
                          <Checkbox checked={selected} color="primary" />
                          <div>
                            <div>
                              <span className="xsTitle">{option.Name}</span>
                            </div>
                            <div className="desc">{option.Code}</div>
                          </div>
                        </>
                      )
                    }}
                  />
                )
              }}
            />
          )}

          <Controller
            name="AccountType"
            label="Account Type"
            fullWidth
            margin="dense"
            ref={register}
            control={control}
            render={({ onChange, onBlur, value }) => {
                return (
                  <Autocomplete
                    multiple
                    value={selectedAccountTypeOptions as AccountType[]}
                    fullWidth
                    options={ACCOUNT_TYPE_OPTIONS}
                    disableCloseOnSelect
                    getOptionLabel={option => `${option?.name}`}
                  PopperComponent={AccCodeDropdownFullWidth}
                  onChange={(value, newValue: any) => {
                    onChange(newValue?.map(x => x?.value))
                  }}
                  renderInput={params => (
                    <TextField
                      {...params}
                      variant="standard"
                      label="Account Type"
                    />
                  )}
                  renderOption={(option, { selected }) => {
                    return (
                      <>
                        <Checkbox checked={selected} color="primary" />
                        <div>
                          <div>
                            <span className="xsTitle">{option.name}</span>
                          </div>
                        </div>
                      </>
                    )
                  }}
                />
              )
            }}
          />
        </CardContents>

        <AccountFooter
          options={[
            {
              name: 'Submit',
              onClick: () => {
                handleSubmit(onSubmit)()
              },
              color: 'primary',
            },
          ]}
        />
      </ContentWrapper>
      <ErrorDialog
        errorDia={errDialog}
        setErrorDia={setErrDialog}
        errorMsg={errMessage}
        errorHeaderMsg={'Error!'}
      />
      {/* FOOTER */}
    </>
  )
}
